import { useEffect, useState } from 'react';

export interface WeatherHour {
  time: string;
  temperatureC: number;
  windSpeedKmh: number;
  windDirectionDeg: number;
  weatherCode: number;
}

export interface WeatherForecast {
  hours: WeatherHour[];
  timezone: string;
}

export enum WeatherStatus {
  IDLE = 'IDLE',
  LOADING = 'LOADING',
  SUCCESS = 'SUCCESS',
  ERROR = 'ERROR',
}

interface WeatherStateIdle {
  status: WeatherStatus.IDLE;
}
interface WeatherStateLoading {
  status: WeatherStatus.LOADING;
}
interface WeatherStateSuccess {
  status: WeatherStatus.SUCCESS;
  data: WeatherForecast;
}
interface WeatherStateError {
  status: WeatherStatus.ERROR;
  message: string;
}

export type WeatherState =
  | WeatherStateIdle
  | WeatherStateLoading
  | WeatherStateSuccess
  | WeatherStateError;

interface OpenMeteoResponse {
  timezone: string;
  hourly: {
    time: string[];
    temperature_2m: number[];
    windspeed_10m: number[];
    winddirection_10m: number[];
    weathercode: number[];
  };
}

type Resolved =
  | { status: WeatherStatus.SUCCESS; lat: number; lon: number; data: WeatherForecast }
  | { status: WeatherStatus.ERROR; lat: number; lon: number; message: string };

export const useWeatherForecast = (lat: number | null, lon: number | null): WeatherState => {
  const [resolved, setResolved] = useState<Resolved | null>(null);

  useEffect(() => {
    if (lat === null || lon === null) return;

    const controller = new AbortController();

    const params = new URLSearchParams({
      latitude: String(lat),
      longitude: String(lon),
      hourly: 'temperature_2m,windspeed_10m,winddirection_10m,weathercode',
      timezone: 'auto',
      forecast_days: '2',
    });

    fetch(`https://api.open-meteo.com/v1/forecast?${params}`, { signal: controller.signal })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json() as Promise<OpenMeteoResponse>;
      })
      .then((json) => {
        const cutoff = Date.now() - 60 * 60 * 1000;
        const hours: WeatherHour[] = json.hourly.time
          .map((time, i) => ({
            time,
            temperatureC: Math.round(json.hourly.temperature_2m[i]),
            windSpeedKmh: Math.round(json.hourly.windspeed_10m[i]),
            windDirectionDeg: json.hourly.winddirection_10m[i],
            weatherCode: json.hourly.weathercode[i],
          }))
          .filter((h) => new Date(h.time).getTime() >= cutoff);
        setResolved({
          status: WeatherStatus.SUCCESS,
          lat,
          lon,
          data: { hours, timezone: json.timezone },
        });
      })
      .catch((err: unknown) => {
        if (err instanceof DOMException && err.name === 'AbortError') return;
        setResolved({
          status: WeatherStatus.ERROR,
          lat,
          lon,
          message: err instanceof Error ? err.message : "Couldn't load the weather. Try again",
        });
      });

    return () => controller.abort();
  }, [lat, lon]);

  if (lat === null || lon === null) return { status: WeatherStatus.IDLE };
  if (resolved && resolved.lat === lat && resolved.lon === lon) {
    return resolved.status === WeatherStatus.SUCCESS
      ? { status: WeatherStatus.SUCCESS, data: resolved.data }
      : { status: WeatherStatus.ERROR, message: resolved.message };
  }
  return { status: WeatherStatus.LOADING };
};

export interface GeoPlace {
  name: string;
  country?: string;
  admin1?: string;
  latitude: number;
  longitude: number;
}

interface OpenMeteoGeoResponse {
  results?: GeoPlace[];
}

export const searchPlaces = async (query: string, signal?: AbortSignal): Promise<GeoPlace[]> => {
  if (!query.trim()) return [];
  const params = new URLSearchParams({ name: query, count: '5', language: 'en', format: 'json' });
  const res = await fetch(`https://geocoding-api.open-meteo.com/v1/search?${params}`, { signal });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const json = (await res.json()) as OpenMeteoGeoResponse;
  return json.results ?? [];
};
